// backButtonLayers.js — o VOLTAR do celular fecha a janela aberta em vez de sair da tela.
//
// POR QUE EXISTE: o voltar do celular (e o gesto de arrastar da borda no iPhone) é o "voltar" do NAVEGADOR. Uma janela
// que abre por cima da tela (painel de conversas do WhatsApp, Configurações, ficha do cliente em tela cheia…) não cria
// página no histórico, então o voltar pulava a janela e saía da tela — na primeira tela depois de entrar, para o login.
//
// COMO:
//   - Abrir uma camada empilha UMA entrada no histórico com o MESMO endereço e a marca `__layer`. O estado do
//     react-router (idx/key) é copiado na entrada: o router vê o mesmo endereço e não navega (ele só lê `state.idx`).
//   - O voltar consome a entrada → a camada é avisada (`onBack`). Se ela resolve por dentro e continua aberta (painel
//     de conversas: da conversa para a lista), devolve `true` e a entrada é recolocada para o próximo voltar.
//   - Fechar pelo X tira a entrada (history.go(-n)), senão o próximo voltar "não faria nada". Várias camadas fechando
//     no mesmo instante viram UM go(-n).
//   - Saiu da tela com a camada aberta (o router empilhou outra página): NÃO volta — desfaria a navegação. A entrada
//     que sobra lá atrás fica "morta" e é pulada sozinha quando o voltar chegar nela.
// Puro sobre `win` (injetável): testado em __tests__/backButtonLayers.test.js com um histórico de mentira.

const MARK = '__layer';

export function createBackButtonLayers(win, {
  schedule = (fn) => win.setTimeout(fn, 0),
  // Prefixo por carregamento da página: depois de recarregar, entradas antigas continuam no histórico com marcas da
  // vida anterior — sem o prefixo, a nova camada nº 1 seria confundida com a nº 1 de antes.
  session = Math.random().toString(36).slice(2, 10),
} = {}) {
  let seq = 0;
  const markers = [];      // [{ id, layer }] — entradas que empilhamos e que estão no histórico (topo = fim)
  let ignorePops = 0;      // popstate provocado por nós (go/back) — não é o voltar do usuário
  let flushScheduled = false;
  let listening = false;

  const currentMark = () => win.history.state?.[MARK] ?? null;

  function pushMarker(layer) {
    seq += 1;
    const id = `${session}:${seq}`;
    const top = markers[markers.length - 1];
    const state = { ...(win.history.state || {}), [MARK]: id };
    // Fechou e reabriu no mesmo instante (a entrada fechada ainda é a atual): reaproveita, não empilha outra.
    if (top && top.layer.closed && currentMark() === top.id) {
      win.history.replaceState(state, '');
      markers[markers.length - 1] = { id, layer };
    } else {
      win.history.pushState(state, '');
      markers.push({ id, layer });
    }
  }

  // Camadas cujas entradas saíram do histórico, do topo para baixo. Devolve se alguma continuou aberta (re-armada).
  function avisarQuemSaiu(sairam) {
    let rearmou = false;
    for (let i = sairam.length - 1; i >= 0; i -= 1) {
      const { layer } = sairam[i];
      if (layer.closed) continue;
      layer.closed = true;   // antes do onBack: o release que o dono chamar ao fechar não pode voltar de novo
      if (layer.onBack() === true) {
        layer.closed = false;
        pushMarker(layer);
        rearmou = true;
      }
    }
    return rearmou;
  }

  function onPopState() {
    if (ignorePops > 0) { ignorePops -= 1; return; }

    const atual = currentMark();
    const pos = atual == null ? -1 : markers.findIndex((m) => m.id === atual);
    if (atual != null && pos === -1) {
      // Entrada morta (camada que fechou depois de uma navegação, ou de antes de recarregar a página). Ela é sempre
      // mais antiga que as nossas, então tudo nosso ficou acima e saiu: avisa. Depois pula a morta (mesmo endereço da
      // página de baixo) para o voltar não parecer travado — sem `ignore`: se a de baixo também for morta, pula de novo.
      if (!avisarQuemSaiu(markers.splice(0))) win.history.back();
      return;
    }

    // Tudo acima da entrada atual saiu do histórico.
    avisarQuemSaiu(markers.splice(pos + 1));
  }

  function flush() {
    flushScheduled = false;
    let fechadasNoTopo = 0;
    while (fechadasNoTopo < markers.length && markers[markers.length - 1 - fechadasNoTopo].layer.closed) {
      fechadasNoTopo += 1;
    }
    if (fechadasNoTopo === 0) return;
    const topId = markers[markers.length - 1].id;
    if (currentMark() === topId) {
      ignorePops += 1;
      win.history.go(-fechadasNoTopo);
    }
    // Se a atual não é a nossa, o usuário saiu da tela: as entradas ficam mortas lá atrás (puladas no onPopState).
    markers.splice(markers.length - fechadasNoTopo);
  }

  /**
   * Camada aberta. `onBack()` = o voltar do celular chegou nela: fechar (devolver nada/false) ou resolver por dentro e
   * continuar aberta (devolver true). Devolve `{ release, isCurrent }`: `release()` ao fechar por qualquer caminho
   * (idempotente); `isCurrent()` = a entrada desta camada é a atual — então navegar para outra tela de dentro dela
   * deve TROCAR a entrada (`navigate(href, { replace: true })`) em vez de empilhar.
   */
  function add(onBack) {
    if (!listening) {
      win.addEventListener('popstate', onPopState);
      listening = true;
    }
    const layer = { onBack, closed: false };
    pushMarker(layer);
    return {
      release() {
        if (layer.closed) return;
        layer.closed = true;
        if (!flushScheduled) {
          flushScheduled = true;
          schedule(flush);
        }
      },
      isCurrent() {
        const top = markers[markers.length - 1];
        return !layer.closed && !!top && top.layer === layer && currentMark() === top.id;
      },
    };
  }

  return { add };
}

// A instância do app (uma só: camadas aninhadas — ficha do cliente → anotação — precisam da mesma pilha).
export const backButtonLayers = typeof window !== 'undefined' ? createBackButtonLayers(window) : null;
